import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
    getReceiveCalendarApi,
    getReceiveDictTreeApi,
    getReceiveByDateApi,
    getReceiveByIdApi,
    getReceiveActivitiesDataApi,
    postReceiveCommentApi,
    putReceiveCommentApi,
    postReceiveActivitiesDataApi,
    getPreplotRListApi,
    getPreplotLinesApi,
    getGeometryOBNApi
} from 'api'
import { BoundarySlice } from 'redux/reducers'

const initialState = {
    calendarInfo: null, // 被点击的日历信息
    calendarData: null, // 日历上当前范围内显示的总结填写信息
    dictData: null, // 数据字典
    listData: null, // 回显数据
    beforeDayData: null, // 上一天回显数据
    locationName: '', // location 名称
    locationId: '', // location id
    save: false,
    id: '',
    beforeDayid: '',
    lineListData: null, // 线列表
}

/* 获取日历上的数据 */
export const getReceiveLandCalendarAction = createAsyncThunk(
    'ReceiveLandSlice/getReceiveCalendarAction',
    async (params, thunkAPI) => {
        try {
            const { data } = await getReceiveCalendarApi(params)
            return data.data
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 获取数据字典 */
export const getReceiveLandDictDataAction = createAsyncThunk(
    'ReceiveLandSlice/getReceiveDictDataAction',
    async (dictType, thunkAPI) => {
        const receiverActivityDataDict = getReceiveDictTreeApi(dictType[0])
        const receiverStatusDataDict = getReceiveDictTreeApi(dictType[1])
        const receiverTypeDataDict = getReceiveDictTreeApi(dictType[2])
        const receiverLineTypeDataDict = getReceiveDictTreeApi(dictType[4])
        const receiverGroupTypeDataDict = getReceiveDictTreeApi(dictType[5]) // 班组
        const preplotList = getPreplotRListApi(dictType[3])
        const geometryOBN = getGeometryOBNApi(dictType[3])
        return Promise.all([receiverActivityDataDict, receiverStatusDataDict, receiverTypeDataDict, preplotList, geometryOBN, receiverLineTypeDataDict, receiverGroupTypeDataDict])
            .then(dictData => dictData)
            .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
    }
)

// 激发文件线
export const getReceiveLandLinesDataAction = createAsyncThunk(
    'AcquisitionSlice/getReceiveLinesDataAction',
    async (id, thunkAPI) => {
        try {
            const lineListData = await getPreplotLinesApi(id) // 通过文件 id 查询
            return lineListData?.data?.data
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 通过日期获取 id */
export const getReceiveLandByDateAction = createAsyncThunk(
    'ReceiveLandSlice/getReceiveByDateAction',
    async (listDataParams, thunkAPI) => {
        try {
            const commentData = await getReceiveByDateApi(listDataParams) // 通过日期查询 id
            return commentData?.data?.data?.id
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 通过日期获取上一天 id */
export const getReceiveLandByBeforeDayDateAction = createAsyncThunk(
    'ReceiveLandSlice/getReceiveLandByBeforeDayDateAction',
    async (listDataParams, thunkAPI) => {
        try {
            const commentData = await getReceiveByDateApi(listDataParams) // 通过日期查询 id
            return commentData?.data?.data?.id
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 获取数据 */
export const getReceiveLandDataAction = createAsyncThunk('ReceiveLandSlice/getReceiveDataAction', async (id, thunkAPI) => {
    const commentData = getReceiveByIdApi(id) // 通过 id 查询
    const activitiesData = getReceiveActivitiesDataApi(id)
    return Promise.all([commentData, activitiesData])
        .then(listData => listData)
        .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
})

/* 获取上一天数据 */
export const getReceiveLandLastDayDataAction = createAsyncThunk('ReceiveLandSlice/getReceiveLandLastDayDataAction', async (id, thunkAPI) => {
    const commentData = getReceiveByIdApi(id) // 通过 id 查询
    const activitiesData = getReceiveActivitiesDataApi(id)
    return Promise.all([commentData, activitiesData])
        .then(listData => listData)
        .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
})

/* 保存数据 */
export const postReceiveLandDataAction = createAsyncThunk(
    'ReceiveLandSlice/postReceiveDataAction',
    async ({ id, commentParams, activitiesParams }, { dispatch }) => {
        let vid
        try {
            if (!id) {
                const commentData = await postReceiveCommentApi(commentParams) // 新增
                vid = commentData.data.data.id
                await postReceiveActivitiesDataApi(activitiesParams, vid)
                dispatch(ReceiveLandSlice.actions.addId(vid))
                return { isTrue: true, id: vid } // 已保存状态
            } else {
                vid = id
                await putReceiveCommentApi(commentParams, id) // 修改
                await postReceiveActivitiesDataApi(activitiesParams, id)
                return { isTrue: true, id } // 已保存状态
            }
        } catch (error) {
            dispatch(ReceiveLandSlice.actions.addId(vid))
            dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

export const ReceiveLandSlice = createSlice({
    name: 'ReceiveLandSlice',
    initialState,
    reducers: {
        /* 日历点击信息 */
        addCalendarInfo: (state, action) => {
            state.calendarInfo = action.payload
        },
        /* 清空日历与详情页信息 */
        clearListData: state => {
            state.listData = null
            state.calendarData = null
            state.save = false
            state.id = ''
            state.beforeDayid = ''
            state.beforeDayData = null
        },
        /* 当前日历主 id */
        addId: (state, action) => {
            state.id = action.payload
        },
        /* location 名称 */
        addLocationName: (state, action) => {
            state.locationName = action.payload
        },
        /* location id */
        addLocationId: (state, action) => {
            state.locationId = action.payload
        },
        /* 清除接收文件数据 */
        clearFielData: (state, action) => {
            state.pointsListData = null
            state.lineListData = null
        },
    },
    extraReducers: {
        /* 获取日历上的数据 */
        [getReceiveLandCalendarAction.fulfilled.type]: (state, action) => {
            state.calendarData = action.payload
        },

        /* 处理数据字典 */
        [getReceiveLandDictDataAction.fulfilled.type]: (state, action) => {
            state.dictData = action.payload
        },

        /* 线 */
        [getReceiveLandLinesDataAction.fulfilled.type]: (state, action) => {
            state.lineListData = action.payload
        },

        /* 提交总结数据 */
        [postReceiveLandDataAction.fulfilled.type]: (state, action) => {
            state.save = action.payload
        },

        /* 获取回显数据 */
        [getReceiveLandDataAction.fulfilled.type]: (state, action) => {
            state.listData = action.payload
        },

        /* 获取上一天回显数据 */
        [getReceiveLandLastDayDataAction.fulfilled.type]: (state, action) => {
            state.beforeDayData = action.payload
        },

        /* 通过日期，获取 id */
        [getReceiveLandByDateAction.fulfilled.type]: (state, action) => {
            state.id = action.payload
        },

        /* 通过日期，获取上一天 id */
        [getReceiveLandByBeforeDayDateAction.fulfilled.type]: (state, action) => {
            state.beforeDayid = action.payload
        },
    },
})
